import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../../../../../ui/modal/modal';

export type ModalOption = {
  label: string;
  modalContent?: JSX.Element | ((closeModal: () => void) => React.ReactNode);
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  value?: string;
  hoverStyle?: 'green' | 'red' | 'indigo';
};

type DropdownWithModalProps = {
  options: ModalOption[];
  disabled?: boolean;
  label?: string;
  className?: string;
  buttonColor?: 'green' | 'indigo' | 'gray';
};

export const DropdownWithModal: React.FC<DropdownWithModalProps> = ({
  options,
  disabled = false,
  label = 'Actions',
  className = '',
  buttonColor = 'indigo',
}) => {
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<ModalOption | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const colorClasses = {
    green: {
      button: 'bg-white text-green-600 shadow-sm hover:shadow border border-green-100 hover:border-green-200',
      icon: 'text-green-500',
      hover: 'hover:bg-green-50 hover:text-green-700',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    },
    indigo: {
      button: 'bg-white text-indigo-600 shadow-sm hover:shadow border border-indigo-100 hover:border-indigo-200',
      icon: 'text-indigo-500',
      hover: 'hover:bg-indigo-50 hover:text-indigo-700',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
    },
    gray: {
      button: 'bg-white text-gray-600 shadow-sm hover:shadow border border-gray-100 hover:border-gray-200',
      icon: 'text-gray-500',
      hover: 'hover:bg-gray-50 hover:text-gray-700',
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
    },
  };

  const resolveModalContent = (): JSX.Element | ((closeModal: () => void) => React.ReactNode) => {
    const foundOption = options.find((o) => o.value === selectedValue);

    if (foundOption?.modalContent) {
      return foundOption.modalContent;
    }
    // Always return a valid JSX.Element to satisfy the ModalProps type
    return <div />;
  };

  const handleOptionSelect = (option: ModalOption) => {
    if (!option.disabled) {
      if (option.modalContent) {
        setSelectedValue(option.value || '');
        setSelectedOption(option);
        setIsModalVisible(true);
      }
      if (option.onClick) {
        option.onClick();
      }
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      setIsModalVisible(false);
    };
  }, []);

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedValue('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const colors = colorClasses[buttonColor];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      { /* Modern dropdown button */ }
      <button
          onClick={toggleDropdown}
          className={`
          flex min-h-10 items-center justify-between gap-2 w-full px-3 sm:px-4 py-2 rounded-lg
          font-medium text-sm transition-all duration-200
          ${disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
            : colors.button
          }
        `}
          disabled={disabled}
      >
        <div className='flex items-center gap-2'>

          <span>{ label }</span>
        </div>
        <svg
            className={`w-5 h-5 ${colors.icon} transform transition-transform duration-200 ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
        >
          <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M19 9l-7 7-7-7'
          ></path>
        </svg>
      </button>

      { /* Dropdown Menu */ }
      { isDropdownOpen && (
        <div className='absolute left-0 right-auto z-50 mt-2 w-[min(220px,calc(100vw-2rem))] bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden sm:left-auto sm:right-0'>
          { options.map((option) => (
            <button
                key={option.value}
                onClick={() => !option.disabled && handleOptionSelect(option)}
                className={`
                w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-left flex items-center gap-3 transition-colors
                ${
                  option.disabled
                    ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                    : `text-gray-700 ${
                      option.hoverStyle === 'green'
                        ? 'hover:bg-green-50 hover:text-green-700'
                        : option.hoverStyle === 'red'
                          ? 'hover:bg-red-50 hover:text-red-700'
                          : option.hoverStyle === 'indigo'
                            ? 'hover:bg-indigo-50 hover:text-indigo-700'
                            : colors.hover
                    }`
                }
                border-b border-gray-100 last:border-b-0
              `}
                disabled={option.disabled}
            >
              { option.icon || (
                <div className={`w-6 h-6 flex items-center justify-center rounded-full ${colors.iconBg}`}>
                  <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className={`h-3 w-3 ${colors.iconColor}`}
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                    />
                  </svg>
                </div>
              ) }
              { option.label }
            </button>
          )) }
        </div>
      ) }

      { /* Modal */ }
      { selectedOption?.modalContent && (
        <Modal
            visible={isModalVisible}
            toggle={closeModal}
            modalContent={resolveModalContent()}
        />
      ) }
    </div>
  );
};

export default DropdownWithModal;
